import { useCallback, useEffect, useState } from 'react';
import { useClient } from 'shared/apollo/hooks';
import { FeedbackDashboardCardProps } from 'shared/components/Feedback/FeedbackDashboardCard';
import { FeedbackApi, feedbackMapFromApi, getFeedback } from '../api';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';

type ReportFeedbackBase = {
  reloadFeedback: () => unknown;
};

type ReportFeedback =
  | ({
      isLoadingFeedback: true;
      feedbackLoadingError: undefined;
      feedback: undefined;
    } & ReportFeedbackBase)
  | ({
      isLoadingFeedback: false;
      feedback: undefined;
      feedbackLoadingError: string;
    } & ReportFeedbackBase)
  | ({
      isLoadingFeedback: false;
      feedback: FeedbackDashboardCardProps;
      feedbackLoadingError: undefined;
    } & ReportFeedbackBase);

const defaultErrorMessage = 'Unable to load feedback.';
export const useReportFeedback = (
  reportId: number | string,
): ReportFeedback => {
  const client = useClient();
  const [feedbackRequestState, makeRequest] = useGraphQLRequest<{
    reportsTodo: FeedbackApi[];
  }>();
  const [feedback, setFeedback] = useState<FeedbackDashboardCardProps>();

  const loadFeedback = useCallback(() => {
    makeRequest(
      getFeedback(
        client!,
        undefined,
        undefined,
        reportId,
        undefined,
        undefined,
      ),
    );
  }, [reportId, makeRequest]);

  useEffect(() => {
    if (feedbackRequestState.type === 'REQUEST_INIT') {
      loadFeedback();
    } else if (feedbackRequestState.type === 'REQUEST_SUCCESS') {
      const feedbacks = feedbackMapFromApi(
        feedbackRequestState.data.reportsTodo,
      ).feedbackRows;
      setFeedback(feedbacks[0]);
    }
  }, [feedbackRequestState]);

  const isLoading =
    feedbackRequestState.type === 'REQUEST_ERROR' ? false : !feedback;
  const isError =
    feedbackRequestState.type === 'REQUEST_ERROR'
      ? feedbackRequestState.error.message || defaultErrorMessage
      : undefined;
  return isLoading
    ? {
        feedback: undefined,
        feedbackLoadingError: undefined,
        reloadFeedback: loadFeedback,
        isLoadingFeedback: true,
      }
    : isError
    ? {
        feedback: undefined,
        feedbackLoadingError: isError,
        reloadFeedback: loadFeedback,
        isLoadingFeedback: false,
      }
    : feedback
    ? {
        feedback,
        feedbackLoadingError: undefined,
        reloadFeedback: loadFeedback,
        isLoadingFeedback: false,
      }
    : {
        feedback: undefined,
        feedbackLoadingError: defaultErrorMessage,
        reloadFeedback: loadFeedback,
        isLoadingFeedback: false,
      };
};
